import { useMemo, useState } from 'react'
import { RotateCcw } from 'lucide-react'
import { Button, Modal } from '@/components/ui'
import { usePreferences } from '@/shell/stores/preferences'

/**
 * Sprint 13b — Sidebar settings section. Surfaces the user's
 * persisted per-section ordering and offers a destructive Reset
 * action that clears `sidebarOrder` and restores manifest defaults.
 *
 * Category (section) ordering was added later as an independent axis —
 * its own Reset action, own confirm modal, own persisted preference
 * (`sidebarSectionOrder`). Resetting one order never touches the other.
 *
 * Per spec D-5 (Claude default), the Reset actions live ONLY here
 * (no kebab in the sidebar header) — they're infrequent and the
 * sidebar should stay clean.
 */
export function SidebarSettingsSection() {
  const sidebarOrder = usePreferences((s) => s.sidebarOrder)
  const resetSidebarOrder = usePreferences((s) => s.resetSidebarOrder)
  const sidebarSectionOrder = usePreferences((s) => s.sidebarSectionOrder)
  const resetSidebarSectionOrder = usePreferences((s) => s.resetSidebarSectionOrder)
  const [moduleResetConfirmOpen, setModuleResetConfirmOpen] = useState(false)
  const [sectionResetConfirmOpen, setSectionResetConfirmOpen] = useState(false)

  const customizedSections = useMemo(() => {
    return Object.entries(sidebarOrder)
      .filter(([, ids]) => Array.isArray(ids) && ids.length > 0)
      .map(([section]) => section)
  }, [sidebarOrder])

  const hasCustomOrder = customizedSections.length > 0
  const hasCustomSectionOrder = sidebarSectionOrder.length > 0

  function handleResetModuleOrder() {
    resetSidebarOrder()
    setModuleResetConfirmOpen(false)
  }

  function handleResetSectionOrder() {
    resetSidebarSectionOrder()
    setSectionResetConfirmOpen(false)
  }

  return (
    <section className="flex flex-col gap-6">
      <header>
        <h2 className="text-role-page-subtitle">Sidebar</h2>
        <p className="mt-1 text-sm text-secondary">
          Module order and category order are saved per user. Drag the
          grip icon next to any module to reorder it within its
          category, or next to a category header to reorder categories
          themselves. Use the actions below to restore defaults.
        </p>
      </header>

      <div className="flex flex-col gap-3 rounded-md border border-border-subtle bg-surface p-4">
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-primary">
            {hasCustomOrder ? 'Custom module order in effect' : 'Default module order in effect'}
          </span>
          <span className="text-xs text-secondary">
            {hasCustomOrder
              ? `You've reordered modules in ${customizedSections.length} section${
                  customizedSections.length === 1 ? '' : 's'
                }: ${customizedSections.join(', ')}.`
              : 'No module ordering has been customized yet. The sidebar follows the default ordering shipped with each module.'}
          </span>
        </div>
        <div>
          <Button
            variant="secondary"
            size="sm"
            disabled={!hasCustomOrder}
            onClick={() => setModuleResetConfirmOpen(true)}
          >
            <RotateCcw size={14} />
            Reset module order
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-3 rounded-md border border-border-subtle bg-surface p-4">
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-primary">
            {hasCustomSectionOrder ? 'Custom category order in effect' : 'Default category order in effect'}
          </span>
          <span className="text-xs text-secondary">
            {hasCustomSectionOrder
              ? "You've reordered the sidebar's top-level categories."
              : 'No category ordering has been customized yet. The sidebar categories follow the default order.'}
          </span>
        </div>
        <div>
          <Button
            variant="secondary"
            size="sm"
            disabled={!hasCustomSectionOrder}
            onClick={() => setSectionResetConfirmOpen(true)}
          >
            <RotateCcw size={14} />
            Reset category order
          </Button>
        </div>
      </div>

      <Modal
        open={moduleResetConfirmOpen}
        onOpenChange={setModuleResetConfirmOpen}
        tone="danger"
        title="Reset module order?"
        description={
          hasCustomOrder
            ? `This will clear your custom ordering across all ${customizedSections.length} section${
                customizedSections.length === 1 ? '' : 's'
              } and restore the default module order. This cannot be undone.`
            : 'No custom ordering is currently saved.'
        }
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setModuleResetConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" size="sm" onClick={handleResetModuleOrder}>
              Reset order
            </Button>
          </>
        }
      />

      <Modal
        open={sectionResetConfirmOpen}
        onOpenChange={setSectionResetConfirmOpen}
        tone="danger"
        title="Reset category order?"
        description={
          hasCustomSectionOrder
            ? 'This will clear your custom category order and restore the default order. This cannot be undone.'
            : 'No custom category order is currently saved.'
        }
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setSectionResetConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" size="sm" onClick={handleResetSectionOrder}>
              Reset order
            </Button>
          </>
        }
      />
    </section>
  )
}
